import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { IonButton } from '@ionic/react';
import { useToast } from 'contexts/ToastContext';
import billingService from 'services/billing';
import Addons from 'services/addons';
import Credits, { CREDIT_CHANGED_EVENT, formatBalance } from 'services/credits';
import AddCreditsModal from 'components/billing/AddCreditsModal';
import CouponModal from 'components/billing/CouponModal';
import AddonCard, { QuantityStepper, CopyButton } from 'components/billing/AddonCard';
import DataTable from '../../components/shared/DataTable';
import './BillingPage.css';

// Heuristic shown under the big balance number.  We picked the Light
// Managed App tier ($0.05/hr) as the "if always on" example — it's the
// cheapest tier so the runtime estimate stays optimistic and is honest
// about being a lower bound. Future work: derive this from the user's
// own currently-running things.
const HEURISTIC_BURN_USD_PER_HOUR = 0.05;
const HEURISTIC_LABEL = 'Light Managed App, always on';

// A day of runway at the heuristic burn rate, expressed in milli-cents
// ($1 = 100_000 milli-cents).  Used to drive the "Add credits to
// enable" inline warning on addon cards — if the user's balance is
// below a single day of even the cheapest baseline, enabling a paid
// addon is almost certain to 402 within hours.
const ONE_DAY_MILLI_CENTS = HEURISTIC_BURN_USD_PER_HOUR * 24 * 100000;

const formatUsd = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0.00';
  return `$${v.toFixed(2)}`;
};

const BillingPage = () => {
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [addonBusy, setAddonBusy] = useState({ egress: false, warm: false });
  const ledgerRef = useRef(null);
  // Mirror of billingData kept in a ref so post-checkout poll intervals
  // can read fresh state without re-rendering the effect closure.
  const billingDataRef = useRef(null);
  const { showToast } = useToast();
  const location = useLocation();

  const loadBillingData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      // bucket='hour' aggregates per-minute Managed App deducts into
      // per-hour rows. Without this, a continuously-running app produces
      // 60 near-identical "managed_app_minute  −$0.0008" rows per hour
      // and the activity table becomes unreadable. Topups, grants, and
      // coupons still render as individual rows.
      const data = await billingService.getBillingInfo({ bucket: 'hour' });
      // Only push to React state when something actually changed —
      // prevents the post-checkout poll loop from re-rendering the
      // same numbers every 1.5s (visible "flash" while waiting on the
      // webhook).
      const prev = billingDataRef.current;
      const changed = !prev
        || prev?.credits?.balanceMilliCents !== data?.credits?.balanceMilliCents
        || (prev?.recentLedger?.length || 0) !== (data?.recentLedger?.length || 0);
      billingDataRef.current = data;
      if (changed) setBillingData(data);
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mount effect: load data + handle Stripe-checkout return.
  // The credits success URL pattern is `?checkout=success&session_id=X`
  // (set in AddCreditsModal). The Stripe webhook arrives ASYNC after
  // the redirect — sometimes a fraction of a second, sometimes 5+ s
  // depending on stripe-cli forward latency / backend cold-start. A
  // single setTimeout was almost always firing before addCredits
  // actually ran, leaving the page stuck at the old balance.
  //
  // Instead: snapshot the current balance, then poll /billing/info
  // every 1.5s for up to 20s, stopping as soon as the balance changes
  // (the webhook posted) or we hit the cap (something's wrong — leave
  // it for the user to refresh).
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutParam = urlParams.get('checkout');

    if (checkoutParam === 'success') {
      try { showToast('Credits added — thanks!', 'success'); } catch {}
      window.history.replaceState({}, document.title, location.pathname);
      // No polling. The webhook is the source of truth — when it fires,
      // CREDIT_CHANGED_EVENT triggers a refetch via the other useEffect
      // below. User can also just refresh the page if needed.
      loadBillingData();
      return;
    }
    loadBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch whenever a credit-affecting event fires (top-up returns,
  // addon enable/disable on another tab). Same hook the header chip
  // uses — keeps everything in sync without polling.
  useEffect(() => {
    const handler = () => loadBillingData();
    window.addEventListener(CREDIT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDIT_CHANGED_EVENT, handler);
  }, [loadBillingData]);

  const credits = billingData?.credits || null;
  const addons = billingData?.addons || { dedicatedEgress: {}, warmPool: {} };
  const topupHistory = billingData?.topupHistory || [];
  const recentLedger = billingData?.recentLedger || [];

  const balanceMc = Number(credits?.balanceMilliCents) || 0;
  const balanceUsd = balanceMc / 100000;
  const hoursOfRuntime = balanceUsd > 0
    ? Math.round(balanceUsd / HEURISTIC_BURN_USD_PER_HOUR)
    : 0;
  const insufficient = balanceMc < ONE_DAY_MILLI_CENTS;

  const scrollToLedger = () => {
    ledgerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Dedicated Egress action handlers ────────────────────────────────
  const onToggleEgress = async () => {
    const isEnabled = !!addons.dedicatedEgress?.enabled;
    setAddonBusy((s) => ({ ...s, egress: true }));
    try {
      if (isEnabled) {
        await Addons.disableDedicatedEgress();
        showToast('Dedicated egress disabled', 'success');
      } else {
        await Addons.enableDedicatedEgress();
        showToast('Dedicated egress enabled — provisioning IP…', 'success');
      }
      Credits.fireChangedEvent();
      await loadBillingData();
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Action failed';
      showToast(msg, 'error');
    } finally {
      setAddonBusy((s) => ({ ...s, egress: false }));
    }
  };

  // ── Warm Pool action handlers ───────────────────────────────────────
  const onToggleWarmPool = async () => {
    const isEnabled = !!addons.warmPool?.enabled;
    setAddonBusy((s) => ({ ...s, warm: true }));
    try {
      if (isEnabled) {
        await Addons.disableWarmPool();
        showToast('Warm pool disabled', 'success');
      } else {
        // Default to 1 instance on first enable — user can scale up
        // with the quantity stepper once it renders enabled.
        await Addons.enableWarmPool(1);
        showToast('Warm pool enabled', 'success');
      }
      Credits.fireChangedEvent();
      await loadBillingData();
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Action failed';
      showToast(msg, 'error');
    } finally {
      setAddonBusy((s) => ({ ...s, warm: false }));
    }
  };

  const onChangeWarmQuantity = async (nextQuantity) => {
    setAddonBusy((s) => ({ ...s, warm: true }));
    try {
      await Addons.enableWarmPool(nextQuantity);
      showToast(`Warm pool scaled to ${nextQuantity}`, 'success');
      Credits.fireChangedEvent();
      await loadBillingData();
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Scale failed';
      showToast(msg, 'error');
    } finally {
      setAddonBusy((s) => ({ ...s, warm: false }));
    }
  };

  // ── Card detail row helpers ─────────────────────────────────────────
  const egress = addons.dedicatedEgress || {};
  const egressPerDay = Number(egress.perDayUsd);
  const egressDetailRows = egress.enabled
    ? [
      ['IP address', egress.eipAddress ? (
        <span className="addon-ip">
          <code>{egress.eipAddress}</code>
          <CopyButton text={egress.eipAddress} />
        </span>
      ) : 'Provisioning…'],
    ]
    : [];

  const warm = addons.warmPool || {};
  const warmQty = Number(warm.quantity) || 0;
  const warmPerDayEach = Number(warm.perInstancePerDayUsd);
  const warmDetailRows = warm.enabled
    ? [
      ['Current instances', warmQty.toString()],
      ...(Number.isFinite(warmPerDayEach)
        ? [['Total per day', `$${(warmPerDayEach * warmQty).toFixed(2)}`]]
        : []),
    ]
    : [];

  return (
    <>
      <Helmet>
        <title>Billing - Zibby</title>
        <meta name="description" content="Manage your Zibby credit balance and add-ons." />
      </Helmet>
      <div className="billing-page page-fade-in main-body page-layout">
        <div className="page-header-center">
          <h1>Billing</h1>
          <p>Credit balance, add-ons, and payment history</p>
        </div>

        {/* ── A. BALANCE CARD ─────────────────────────────────────────── */}
        <div className="page-card balance-hero">
          <div className="balance-hero-body">
            <div className="balance-hero-label">Balance</div>
            <div className={`balance-hero-amount ${balanceMc < 0 ? 'is-paused' : balanceMc < 100000 ? 'is-low' : ''}`}>
              {loading ? '—' : formatBalance(balanceMc)}
            </div>
            <div className="balance-hero-runtime">
              {loading
                ? ' '
                : balanceUsd > 0
                  ? 'Burn rate depends on what you run — see rate card below.'
                  : 'Add credits to start running workflows and apps.'}
            </div>
          </div>
          <div className="balance-hero-actions">
            {/* Single CTA — Recent activity section below shows the ledger,
                so a "View history" button here is redundant. The shared
                .btn-white atom is an IonButton class — its --background
                etc. CSS vars don't apply to a plain <button> so we use
                IonButton here to match the project-settings + sentry
                page pattern. */}
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={() => setAddCreditsOpen(true)}
            >
              Add credits
            </IonButton>
            {/* Promo-code redemption — secondary, doesn't compete with the
                primary CTA. Opens a sibling modal; success refreshes the
                balance via the global credit-changed event the modal fires. */}
            <button
              type="button"
              className="balance-hero-coupon-link"
              onClick={() => setCouponOpen(true)}
            >
              Use coupon
            </button>
          </div>
        </div>

        {/* ── B. ADDONS SECTION ──────────────────────────────────────── */}
        <div className="page-card addons-section">
          {/* Title + blurb live on the same row — blurb sits at top-right
              aligned with the "Add-ons" heading. No border between
              header and grid. */}
          <div className="addons-section-header">
            <h2>Add-ons</h2>
            <p className="addons-section-blurb">
              Optional infrastructure billed per-minute from your credit balance. Disable any time.
            </p>
          </div>

          <div className="addons-grid">
            <AddonCard
              name="Dedicated Static IP"
              description="Pin all outbound traffic to one static IP so you can allowlist Zibby on a firewalled GitLab / GitHub Enterprise / Salesforce."
              perDayUsd={Number.isFinite(egressPerDay) ? egressPerDay : 1.67}
              perMonthEstimateUsd={Number.isFinite(egressPerDay) ? egressPerDay * 30 : 50}
              enabled={!!egress.enabled}
              detailRows={egressDetailRows}
              insufficientBalance={insufficient}
              busy={addonBusy.egress}
              onEnable={onToggleEgress}
              onDisable={onToggleEgress}
              onAddCredits={() => setAddCreditsOpen(true)}
            />

            <AddonCard
              name="Warm Pool"
              description="Pre-running Fargate tasks so workflow runs skip the ~15s cold-start. Charged per-instance, per-minute."
              perDayUsd={Number.isFinite(warmPerDayEach) ? warmPerDayEach : 0.83}
              perMonthEstimateUsd={Number.isFinite(warmPerDayEach) ? warmPerDayEach * 30 : 25}
              enabled={!!warm.enabled}
              detailRows={warmDetailRows}
              insufficientBalance={insufficient}
              busy={addonBusy.warm}
              onEnable={onToggleWarmPool}
              onDisable={onToggleWarmPool}
              onAddCredits={() => setAddCreditsOpen(true)}
            >
              {warm.enabled && (
                <div className="addon-card-quantity-row">
                  <span className="addon-card-detail-label">Instances</span>
                  <QuantityStepper
                    value={warmQty || 1}
                    min={1}
                    max={10}
                    onChange={onChangeWarmQuantity}
                    busy={addonBusy.warm}
                  />
                </div>
              )}
            </AddonCard>
          </div>
        </div>

        {/* ── C. RATE CARD ───────────────────────────────────────────── */}
        {/* Both tables on this page render via the shared DataTable atom
            (components/shared/DataTable) — same component the Projects /
            Tokens / Pipelines pages use. No bespoke table styles. */}
        <div className="page-card">
          <div className="card-header">
            <h2>Rate card</h2>
          </div>
          <DataTable
            rowKey="label"
            columns={[
              { header: 'Resource', key: 'label',  render: (r) => r.label },
              { header: 'Billing',  key: 'basis',  render: (r) => r.basis },
              { header: 'Rate',     key: 'rate',   render: (r) => r.rate },
            ]}
            data={(() => {
              const rates = credits?.activeRates || {};
              const wf = rates.workflowRun || {};
              const ma = rates.managedApp || {};
              const perMin = (mc) => `$${((Number(mc) || 0) / 100000).toFixed(4)}/min`;
              const perHr  = (mc) => `$${((Number(mc) || 0) * 60 / 100000).toFixed(2)}/hr`;
              return [
                { label: 'Workflow run — cold (new Fargate task)',     basis: 'per minute', rate: wf.cold ? `${perMin(wf.cold)} (~ ${perHr(wf.cold)})` : '—' },
                { label: 'Workflow run — warm (reused task)',          basis: 'per minute', rate: wf.warm ? `${perMin(wf.warm)} (~ ${perHr(wf.warm)})` : '—' },
                { label: 'Managed App — Light tier',     basis: 'per minute', rate: ma.light    ? perHr(ma.light)    : '—' },
                { label: 'Managed App — Standard tier',  basis: 'per minute', rate: ma.standard ? perHr(ma.standard) : '—' },
                { label: 'Managed App — Heavy tier',     basis: 'per minute', rate: ma.heavy    ? perHr(ma.heavy)    : '—' },
              ];
            })()}
          />
        </div>

        {/* ── D. LEDGER (everything credit-affecting) ────────────────── */}
        <div className="page-card" ref={ledgerRef}>
          <div className="card-header">
            <h2>Recent activity</h2>
          </div>
          <DataTable
            rowKey="id"
            loading={loading}
            columns={[
              { header: 'When',   key: 'when',   render: (r) => {
                if (!r.ts) return '—';
                const when = new Date(r.ts).toLocaleString();
                // For hour-bucketed rows, show "17:00 — 17:59" so the user
                // sees the hour window instead of a misleading single
                // timestamp that's actually a sum over 60 minutes.
                if (r.bucket === 'hour') {
                  const start = new Date(r.ts);
                  const end = new Date(start.getTime() + 3599999);
                  const fmt = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  const dateStr = start.toLocaleDateString();
                  return `${dateStr}, ${fmt(start)}–${fmt(end)}`;
                }
                return when;
              }},
              { header: 'Reason', key: 'reason', render: (r) => {
                const reason = r.reason || '—';
                // `× N` badge tells the user how many underlying rows were
                // folded into this bucket. Subtle styling so it reads as
                // metadata, not a primary identifier.
                if (r.count > 1) {
                  return (
                    <span>
                      {reason}
                      <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.45)', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                        × {r.count}
                      </span>
                    </span>
                  );
                }
                return reason;
              }},
              { header: 'Source', key: 'source', render: (r) => r.source || '—' },
              { header: 'Amount', key: 'amount', render: (r) => {
                const delta = Number(r.deltaMilliCents) || 0;
                const isCredit = delta > 0;
                // 'auto' precision keeps cent-level values clean ($0.05)
                // while showing sub-cent deductions at 4dp ($0.0008) so
                // per-minute Managed App charges don't all read as "$0.00".
                return (
                  <span style={{ color: isCredit ? '#4ade80' : '#f87171', fontVariantNumeric: 'tabular-nums' }}>
                    {isCredit ? '+' : ''}{formatBalance(delta, { precision: 'auto' })}
                  </span>
                );
              }},
            ]}
            data={recentLedger.map((r, i) => ({ ...r, id: r.id || `${r.ts || ''}-${i}` }))}
            emptyState={{
              title: 'No activity yet',
              description: 'Top up credits or run workflows / apps to see entries here.',
            }}
          />
        </div>

        {/* Add-credits modal — single instance, opened from balance hero
            or from the inline "Add credits" CTA on insufficient-balance
            addon warnings. */}
        {addCreditsOpen && (
          <AddCreditsModal
            isOpen={addCreditsOpen}
            onClose={() => setAddCreditsOpen(false)}
          />
        )}

        {/* Coupon redemption modal — fires CREDIT_CHANGED_EVENT on success,
            so the balance hero, header chip, and ledger all refresh
            automatically. */}
        {couponOpen && (
          <CouponModal
            isOpen={couponOpen}
            onClose={() => setCouponOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default BillingPage;
